/* eslint-disable @typescript-eslint/no-explicit-any */

import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "../buttons/Button";
import { errorAlert, makeApiRequest, successAlert } from "../../util";
import AuthContext from "../../store/authContext";
import { ADD_ORGANIZATION_MAPPING_FORM_FIELDS } from "../../constants";
import Form from "../../ui/Form/Form";
import TextField from "../inputs/TextField";
import { IOrganizationMappingData } from "../../types";
import DeleteModal, { DeleteModalIcons } from "../modal/DeleteModal";
import { Page, Lang, Operation } from "../../translations/types";
import {
  FORM_VALIDATION_TRANSLATIONS,
  getPageTranslationText,
} from "../../translations/util";

const translations = getPageTranslationText({
  page: Page.ORG_MAPPING,
  lang: Lang.EN,
  op: Operation.CREATE,
});

const set = new Set();

const isInValidNumberForField = (val?: number, min?: number, max?: number) => {
  if (val || val === 0) {
    return val < (min || 0) || val > (max || 1048575);
  }

  return true;
};

const isFormInvalid = ({ data }: { data: any }) => {
  if (!data) return true;

  const isConnectionFormInvalid = ADD_ORGANIZATION_MAPPING_FORM_FIELDS.map(
    (rows: any) => {
      const res = rows.fields.map((field: any) => {
        switch (field.type) {
          case "TEXT":
            return field.isInputInvalid?.(data[field.name]);
          case "NUMBER":
            return isInValidNumberForField(
              data[field.name],
              field.min,
              field.max
            );
          default:
            return false;
        }
      });

      return res.reduce((acc: any, el: any) => acc || el);
    }
  ).reduce((acc: any, el: any) => acc || el);

  return isConnectionFormInvalid;
};

const NewOrganizationMappingForm = () => {
  const { token } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  useEffect(() => {
    set.clear();
    return () => {
      set.clear();
    };
  }, []);

  const [connectionData, setConnectionData] =
    useState<IOrganizationMappingData>({
      "ORG-ID": undefined,
      "IWF-ID": undefined,
    });

  const createMapping = async () => {
    setLoading(true);
    try {
      await makeApiRequest<{ Ref: string }>({
        path: `/api/v1/app-config/mappings`,
        method: "POST",
        body: JSON.stringify({
          "IWF-ID": connectionData["IWF-ID"],
          "ORG-ID": connectionData["ORG-ID"],
        }),
        token,
        emptyResponse: true,
      });
      successAlert(translations.sucessMessage);
      navigate("/configurationManagement/organizationmapping");
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      try {
        errorAlert(e);
      } catch (e) {
        errorAlert(translations.errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="data-form">
        <Form>
          <div className="data-tabcontent data-card">
            {ADD_ORGANIZATION_MAPPING_FORM_FIELDS.map(
              (rows: {
                cols: number;
                fields: {
                  name: keyof IOrganizationMappingData;
                  tooltipText?: string;
                  label: string;
                  type: string;
                  invalidText?: string | null;
                  isInputInvalid?: (val?: string) => boolean;
                  min?: number;
                  max?: number;
                }[];
              }) => {
                return (
                  <div
                    className={`grid grid-cols-${rows.cols} gap-4 form-control`}
                  >
                    {rows.fields.map((field) => {
                      return (
                        <TextField
                          onChange={(val) => {
                            set.add(field.name);
                            setConnectionData({
                              ...connectionData,
                              [field.name]: val,
                            });
                          }}
                          placeholder={field.label}
                          value={connectionData[field.name] as string}
                          invalid={
                            set.has(field.name) &&
                            field.isInputInvalid?.(
                              connectionData[field.name] as string
                            )
                          }
                          invalidText={
                            field.invalidText ||
                            FORM_VALIDATION_TRANSLATIONS.required(field.label)
                          }
                          labelText={field.label}
                          tooltipText={field.tooltipText}
                          required
                        />
                      );
                    })}
                  </div>
                );
              }
            )}
          </div>
        </Form>
      </div>
      <DeleteModal
        modalHeaderText={translations.draftAbortModal.header}
        modalDescriptionText={translations.draftAbortModal.description}
        confirmationButtonLabel={
          translations.draftAbortModal.confirmButtonLabel
        }
        onDelete={() =>
          navigate("/configurationManagement/organizationmapping")
        }
        open={open}
        setOpen={setOpen}
        cancelIcon={DeleteModalIcons.Cancel}
      />
      <div className="flex justify-end form-footer">
        <div className="mr-2">
          <Button
            className="w-full"
            type="button"
            kind="tertiary"
            disabled={false}
            label={translations.buttonLabels.cancel}
            onClick={() => setOpen(true)}
            dataCy="cancelCreateOrgMapping-"
          />
        </div>

        <div>
          <Button
            className="w-full"
            type="button"
            loading={loading}
            disabled={isFormInvalid({ data: connectionData })}
            onClick={createMapping}
            label={translations.buttonLabels.create}
            dataCy="createOrgMapping-"
          />
        </div>
      </div>
    </>
  );
};

export default NewOrganizationMappingForm;
